import { useState, useEffect, useCallback } from 'react'
import posthog from 'posthog-js'
import { FeatureFlagKeys } from '@hapana/shared/constants/feature-flag.constants'

import { useFeatureFlagOverride } from '../providers/feature-flag-overrides.provider'
import { getFlagOverride } from '../utils/feature-flag-overrides.utils'

export type FeatureFlagValue = string | number | boolean | null

export interface UseFeatureFlagOptions {
  defaultValue?: FeatureFlagValue
  sendUsageEvent?: boolean
  eventProperties?: Record<string, any>
}

export interface UseFeatureFlagReturn {
  value: FeatureFlagValue
  isLoading: boolean
  isReady: boolean
  refresh: () => void
}

// Access the PostHog instance — only return it if init() has been called
// and feature flags are loaded. The posthog-js module export is always non-null
// (it's a stub before init), so we check __loaded to avoid false readiness.
const getPostHog = () => {
  if (
    posthog &&
    typeof posthog.getFeatureFlag === 'function' &&
    (posthog as any).__loaded === true
  ) {
    return posthog
  }
  return null
}

// The flag key is the PostHog flag NAME — i.e. a FeatureFlagKeys *value*
// (`FeatureFlagKeys.AnalyticsTabEnabled` → 'analytics-tab-enabled'), which is
// what `posthog.getFeatureFlag` is given verbatim below. This was previously
// typed as `keyof typeof FeatureFlagKeys` (the enum's KEY names), which every
// one of the ~40 call sites contradicts; it went unnoticed because they are all
// .js/.jsx and therefore untyped. Typing it as the value union makes the
// existing usage correct and stops a .tsx caller being pushed into passing a
// key name, which would query PostHog for a flag that does not exist.
export const useFeatureFlag = <T extends (typeof FeatureFlagKeys)[keyof typeof FeatureFlagKeys]>(
  flagKey: T,
  options: UseFeatureFlagOptions = {}
): UseFeatureFlagReturn => {
  const { defaultValue = null, sendUsageEvent = false, eventProperties = {} } = options

  // Subtree override (FeatureFlagOverridesProvider) wins over PostHog — the
  // remaining hooks still run unconditionally to satisfy the rules of hooks.
  const override = useFeatureFlagOverride(flagKey) ?? getFlagOverride(flagKey)

  const [value, setValue] = useState<FeatureFlagValue>(defaultValue)
  const [isLoading, setIsLoading] = useState(true)
  const [isReady, setIsReady] = useState(false)

  const checkFlag = useCallback(() => {
    const posthog = getPostHog()

    if (!posthog || typeof posthog.getFeatureFlag !== 'function') {
      setValue(defaultValue)
      setIsLoading(false)
      setIsReady(false)
      return
    }

    try {
      const flagValue = posthog.getFeatureFlag(flagKey)
      setValue(flagValue !== undefined ? flagValue : defaultValue)
      setIsReady(true)

      if (sendUsageEvent && typeof posthog.capture === 'function') {
        posthog.capture('feature_flag_checked', {
          flag_key: flagKey,
          flag_value: flagValue,
          default_value: defaultValue,
          ...eventProperties,
        })
      }
    } catch (error) {
      console.warn(`Error checking feature flag "${flagKey}":`, error)
      setValue(defaultValue)
      setIsReady(false)
    } finally {
      setIsLoading(false)
    }
  }, [flagKey, defaultValue, sendUsageEvent, eventProperties])

  const refresh = useCallback(() => {
    setIsLoading(true)
    checkFlag()
  }, [checkFlag])

  useEffect(() => {
    const posthog = getPostHog()

    if (!posthog) {
      // PostHog not initialized yet — wait for it via polling
      setValue(defaultValue)
      setIsLoading(true)
      setIsReady(false)

      const pollInterval = setInterval(() => {
        if (getPostHog()) {
          clearInterval(pollInterval)
          checkFlag()
        }
      }, 500)

      return () => clearInterval(pollInterval)
    }

    checkFlag()

    // Subscribe to flag changes and capture unsubscribe for cleanup.
    // Without cleanup, listeners accumulate on every effect re-run.
    let unsub: (() => void) | undefined
    if (typeof posthog.onFeatureFlags === 'function') {
      unsub = posthog.onFeatureFlags(() => checkFlag())
    }

    return () => {
      unsub?.()
    }
  }, [checkFlag, defaultValue])

  if (override !== undefined) {
    return {
      value: override,
      isLoading: false,
      isReady: true,
      refresh: () => undefined,
    }
  }

  return {
    value,
    isLoading,
    isReady,
    refresh,
  }
}
